import React, { useEffect, useState } from 'react';
import { IAnalyticsFilters, IAnalyticsDriversResponse } from '@/interfaces/Dashboard/dashboard.interface';
import { AnalyticsExportButton } from './AnalyticsExportButton';
import { CommonCard } from '@/components/common/CommonCard';
import { SectionCard } from '@/components/common/SectionCard';
import { Truck, CheckCircle2, Clock, MapPin, DollarSign, Star, XCircle, UserCheck } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { CommonCardSkeleton } from '@/components/ui/Loaders/DashboardSkeletons';
import { EmptyChart } from '@/components/common/EmptyChart';
import { NoDataFound } from '@/components/common/NoDataFound';
import CommonTable from '@/components/common/CommonTable';
import { formatRand } from '@/utility/helper';

interface DriversSectionProps {
    filters: IAnalyticsFilters;
    data: IAnalyticsDriversResponse['data'] | null;
    isLoading: boolean;
}


export const DriversSection: React.FC<DriversSectionProps> = ({ filters, data, isLoading }) => {
    
    if (isLoading) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="flex justify-between items-center mb-4">
                    <div className="h-6 w-32 bg-gray-200 rounded"></div>
                    <div className="h-8 w-24 bg-gray-200 rounded"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {Array.from({ length: 4 }).map((_, i) => <CommonCardSkeleton key={i} />)}
                </div>
            </div>
        );
    }

    const kpis = data?.kpis || {} as any;
    const charts = data?.charts || {} as any;
    const tables = data?.tables || {} as any;

    const kpiCards = [
        { label: 'Total Drivers', value: kpis.totalDrivers ?? 0, icon: <Truck />, color: '#3B82F6' },
        { label: 'Online Drivers', value: kpis.onlineDrivers ?? 0, icon: <UserCheck />, color: '#10B981' },
        { label: 'Busy Drivers', value: kpis.busyDrivers ?? 0, icon: <Clock />, color: '#F59E0B' },
        { label: 'Offline Drivers', value: kpis.offlineDrivers ?? 0, icon: <XCircle />, color: '#6B7280' },
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-foreground">Driver Performance</h2>
                <AnalyticsExportButton
                    exportUrl="/reports/analytics/export?type=drivers"
                    fileName="driver_performance.xlsx"
                    filters={filters}
                />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {kpiCards.map((card, i) => (
                    <CommonCard key={i} label={card.label} value={card.value} icon={card.icon} iconColor={card.color} />
                ))}
            </div>

            <div className="grid grid-cols-1 gap-4">
                <SectionCard title="Deliveries by Driver" icon={<Truck />}>
                    <div className="h-[300px] w-full">
                        {!charts.deliveriesByDriver || charts.deliveriesByDriver.length === 0 ? (
                            <EmptyChart height="h-[300px]" />
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={charts.deliveriesByDriver}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                <XAxis dataKey="driverName" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} />
                                <Tooltip
                                    cursor={{ fill: '#F3F4F6' }}
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                />
                                <Bar dataKey="deliveries" fill="#3B82F6" radius={[4, 4, 0, 0]} barSize={40} />
                            </BarChart>
                        </ResponsiveContainer>
                        )}
                    </div>
                </SectionCard>
            </div>
            
            <SectionCard title="Driver Performance Ranking" className="overflow-x-auto">
                <CommonTable 
                    columns={[
                        { key: 'driverName', header: 'Driver Name', render: (row: any, idx: number) => (
                            <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">{idx + 1}</div>
                                {row.driverName}
                            </div>
                        ), className: 'font-medium text-foreground' },
                        { key: 'deliveriesCompleted', header: 'Deliveries', className: 'text-right font-medium', headerClassName: 'text-right' },
                        { key: 'acceptanceRate', header: 'Acceptance', render: (row: any) => `${row.acceptanceRate}%`, className: 'text-right text-success', headerClassName: 'text-right' },
                        { key: 'rejectionRate', header: 'Rejection', render: (row: any) => `${row.rejectionRate}%`, className: 'text-right text-destructive', headerClassName: 'text-right' },
                        { key: 'avgDeliveryTime', header: 'Avg Time', render: (row: any) => `${row.avgDeliveryTime}m`, className: 'text-right', headerClassName: 'text-right' },
                        { key: 'distanceTravelled', header: 'Distance (km)', className: 'text-right text-muted-foreground', headerClassName: 'text-right' },
                        { key: 'earnings', header: 'Earnings', render: (row: any) => formatRand(row.earnings), className: 'text-right text-success font-medium', headerClassName: 'text-right' },
                        { key: 'rating', header: 'Rating', render: (row: any) => <div className="flex items-center justify-end gap-1"><Star className="w-4 h-4 fill-amber-500 text-amber-500"/> {row.rating}</div>, className: 'text-right', headerClassName: 'text-right' }
                    ]}
                    data={tables.topDrivers || []}
                />
            </SectionCard>
        </div>
    );
};
